import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";

interface UseSupplierNotificationBarStatusResult {
  isUnreadNotification: boolean;
  isNewNotifications: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Supplier-scoped counterpart of `useNotificationBarStatus`. No real-time
 * push yet — `NotificationType.UPDATED_NOTIFICATION_BAR` only carries a
 * `showId`, so there is nothing to subscribe to for a Supplier-company scope
 * until the backend adds one. Falls back to the query's own `staleTime`
 * refetching, same as any other polled count.
 */
export const useSupplierNotificationBarStatus = (
  supplierCompanyId: string
): UseSupplierNotificationBarStatusResult => {
  const { data, refetch } = useQuery({
    queryKey: [
      RequestKey.GetNotificationBarCount,
      "supplier",
      supplierCompanyId,
    ],
    queryFn: () => apiV2.notificationBar.getCount({ supplierCompanyId }),
    enabled: !!supplierCompanyId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const isUnreadNotification = Boolean(data?.unread);
  const isNewNotifications = Boolean(data?.opened) || Boolean(data?.unread);

  return {
    isUnreadNotification,
    isNewNotifications,
    refetch,
  };
};

export default useSupplierNotificationBarStatus;
